import React from 'react';
import { Table, Badge, Button, Avatar } from '@/components/ui';
import type { MaintenanceCost, CostType, CostStatus } from '../types/maintenance-costs.types';

export interface CostTableProps {
  data: MaintenanceCost[];
  isLoading: boolean;
  onEdit: (item: MaintenanceCost) => void;
  onRowClick: (item: MaintenanceCost) => void;
}

const statusColors: Record<CostStatus, string> = {
  pending: 'bg-amber-100 text-amber-600',
  verified: 'bg-blue-100 text-blue-600',
  approved: 'bg-emerald-100 text-emerald-600',
  rejected: 'bg-red-100 text-red-600',
};

const statusLabels: Record<CostStatus, string> = {
  pending: 'Chờ duyệt',
  verified: 'Đã xác minh',
  approved: 'Đã duyệt',
  rejected: 'Từ chối',
};

const typeColors: Record<CostType, string> = {
  labor: 'bg-slate-100 text-slate-500',
  material: 'bg-emerald-100 text-emerald-600',
  equipment: 'bg-blue-100 text-blue-600',
  other: 'bg-indigo-100 text-indigo-600',
};

export function CostTable({ data, isLoading, onEdit, onRowClick }: CostTableProps) {
  const formatCurrency = (val: number) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(val);

  return (
    <Table
      columns={[
        { key: 'date', title: 'Ngày' },
        { key: 'category', title: 'Mục chi phí' },
        { key: 'type', title: 'Phân loại', align: 'center' },
        { key: 'amount', title: 'Số tiền', align: 'right' },
        { key: 'status', title: 'Trạng thái', align: 'center' },
        { key: 'actions', title: '', align: 'right' },
      ]}
      data={data}
      isLoading={isLoading}
      onRowClick={onRowClick}
      renderCell={(item, key) => {
        switch (key) {
          case 'date':
            return <span className="text-xs font-bold text-slate-700">{new Date(item.cost_date).toLocaleDateString('vi-VN')}</span>;
          case 'category':
            return (
              <div className="flex flex-col">
                <span className="font-bold text-slate-900">{item.category}</span>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[10px] text-os-primary font-bold ">{item.reference_code || '---'}</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-[10px] text-slate-500 font-medium">{item.project?.name}</span>
                </div>
              </div>
            );
          case 'type':
            return (
              <Badge className={`${typeColors[item.cost_type]} border-transparent text-[10px] font-bold  px-2`}>
                {item.cost_type}
              </Badge>
            );
          case 'amount':
            return (
              <div className="flex flex-col items-end">
                <span className="text-sm font-bold text-slate-900">{formatCurrency(item.total_amount)}</span>
                <span className="text-[9px] text-slate-400  font-bold tracking-tighter">{item.quantity} x {item.unit_price.toLocaleString()}</span>
              </div>
            );
          case 'status':
            return (
              <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold `}>
                {statusLabels[item.status_code]}
              </Badge>
            );
          case 'actions':
            return (
              <div className="flex justify-end gap-1">
                <Button variant="ghost" size="sm" icon="ri-edit-line" onClick={(e) => { e.stopPropagation(); onEdit(item); }} />
              </div>
            );
          default:
            return null;
        }
      }}
    />
  );
}
